import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "entity", "select", "template"]

  connect() {
    this.handleDocumentClick = this.handleDocumentClick.bind(this)
    this.handleInputFocus = this.handleInputFocus.bind(this)
    this.handleInputBlur = this.handleInputBlur.bind(this)
    this.handleResultsMouseDown = this.handleResultsMouseDown.bind(this)
    this.handleResultsClick = this.handleResultsClick.bind(this)

    this.autocompleteInputElement()?.addEventListener("focus", this.handleInputFocus)
    this.autocompleteInputElement()?.addEventListener("blur", this.handleInputBlur)
    document.addEventListener("click", this.handleDocumentClick)

    const results = this.autocompleteResultsElement()
    if (results) {
      results.addEventListener("mousedown", this.handleResultsMouseDown)
      results.addEventListener("click", this.handleResultsClick)
      this.resultsObserver = new MutationObserver(() => this.syncAutocompleteDropdownVisibility())
      this.resultsObserver.observe(results, { childList: true, subtree: true })
    }
  }

  disconnect() {
    this.autocompleteInputElement()?.removeEventListener("focus", this.handleInputFocus)
    this.autocompleteInputElement()?.removeEventListener("blur", this.handleInputBlur)
    document.removeEventListener("click", this.handleDocumentClick)
    this.autocompleteResultsElement()?.removeEventListener("mousedown", this.handleResultsMouseDown)
    this.autocompleteResultsElement()?.removeEventListener("click", this.handleResultsClick)
    this.resultsObserver?.disconnect()
  }

  addBusinessEntityFilter(event) {
    const { businessEntityId, businessEntityName } = event.params
    if (!businessEntityId || !businessEntityName || !this.hasTemplateTarget || !this.hasContainerTarget) return
    if (this.entityTargets.some((element) => element.dataset.businessEntityId === businessEntityId.toString())) return

    const item = this.templateTarget.content.firstElementChild.cloneNode(true)
    const hiddenInput = item.querySelector('input[type="hidden"]')
    const nameSpan = item.querySelector("span")
    const icon = item.querySelector("i")
    if (!hiddenInput || !nameSpan || !icon) return

    item.dataset.businessEntityId = businessEntityId
    hiddenInput.value = businessEntityId
    nameSpan.textContent = businessEntityName
    icon.dataset.userAccountCompanyUserIndexBusinessEntityIdParam = businessEntityId

    this.containerTarget.appendChild(item)
    this.interactingWithDropdown = false
    this.hideAutocompleteDropdown()
  }

  removeBusinessEntityFilter(event) {
    event.preventDefault()

    const entityElement = this.entityTargets.find((element) => {
      return element.dataset.businessEntityId === event.params.businessEntityId?.toString()
    })

    if (entityElement) {
      entityElement.remove()
    }
  }

  remove(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.businessEntityFilterIdParam
    if (!id || !this.hasSelectTarget) return

    const option = Array.from(this.selectTarget.options).find(opt => opt.value === id)
    if (option) {
      option.selected = false
      this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  closeAutocomplete() {
    const results = this.autocompleteResultsElement()
    if (results) {
      results.innerHTML = ""
    }

    this.hideAutocompleteDropdown()
  }

  closeBusinessEntityFilter() {
    this.closeAutocomplete()
    const popover = this.element.querySelector(".popover-card")
    if (popover) {
      popover.classList.add("d-none")
    }
  }

  handleInputFocus() {
    this.syncAutocompleteDropdownVisibility()
  }

  handleInputBlur() {
    requestAnimationFrame(() => {
      if (!this.interactingWithDropdown && !this.isFocusInsideAutocomplete()) {
        this.hideAutocompleteDropdown()
      }
    })
  }

  handleDocumentClick(event) {
    if (!this.element.contains(event.target)) {
      this.hideAutocompleteDropdown()
    }
  }

  handleResultsMouseDown(event) {
    if (event.target.closest(".c--pg-autocomplete--suggestion--item")) {
      this.interactingWithDropdown = true
    }
  }

  handleResultsClick() {
    this.interactingWithDropdown = false
  }

  syncAutocompleteDropdownVisibility() {
    if (this.isFocusInsideAutocomplete() && this.hasAutocompleteResults()) {
      this.showAutocompleteDropdown()
    } else {
      this.hideAutocompleteDropdown()
    }
  }

  showAutocompleteDropdown() {
    const dropdown = this.autocompleteDropdownElement()
    if (dropdown) {
      dropdown.style.display = "block"
    }
  }

  hideAutocompleteDropdown() {
    const dropdown = this.autocompleteDropdownElement()
    if (dropdown) {
      dropdown.style.display = "none"
    }
  }

  isFocusInsideAutocomplete() {
    const activeElement = document.activeElement
    const input = this.autocompleteInputElement()
    const dropdown = this.autocompleteDropdownElement()

    return activeElement === input || !!dropdown?.contains(activeElement)
  }

  hasAutocompleteResults() {
    const dropdown = this.autocompleteDropdownElement()
    return !!dropdown && dropdown.children.length > 0
  }

  autocompleteInputElement() {
    return document.getElementById("filter_business_entities_filter_input")
  }

  autocompleteResultsElement() {
    return document.getElementById("pg-autocomplete-result-user_account/filter_business_entities")
  }

  autocompleteDropdownElement() {
    return document.getElementById("c--pg-autocomplete-dropdown--filter_business_entities")
  }
}
